import { useCallback, useState } from "react";

import { CopyIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "./Chips";

type PointOfContactRole = NonNullable<
  NonNullable<OpenAPI.Schemas["Organisation"]["pointOfContactRoles"]>[number]
> & { ownerAssignedTo?: OpenAPI.Schemas["PreparedUser"]; assignedTo?: string };

const PointOfContactList = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const organisation = user?.ownerOrganisation;
  const pointsOfContact = organisation?.pointOfContactRoles as
    | PointOfContactRole[]
    | undefined;

  const [selectedRole, setSelectedRole] = useState<PointOfContactRole | null>(
    null,
  );
  const onMessageThreadSelected = useCallback(
    (thread: OpenAPI.Schemas["MessageThread"], isPhone = false) =>
      dispatch(actions.messages.selectThread(thread, isPhone)),
    [dispatch],
  );

  const createChat = useCallback(
    (users: string[]) => {
      api.messages.createThread(
        { users },
        (thread) => {
          onMessageThreadSelected(thread);
        },
        () => {},
      );
    },
    [onMessageThreadSelected],
  );

  const assignedUserEmail = selectedRole?.ownerAssignedTo?.emails?.[0]?.address;

  if (!pointsOfContact || pointsOfContact.length === 0) {
    return null;
  }

  return (
    <Message>
      <Modal
        open={!!selectedRole}
        onClose={() => {
          setSelectedRole(null);
        }}
      >
        <ModalHeader>
          Contact {selectedRole?.ownerAssignedTo?.profile?.fullName}
        </ModalHeader>
        <ModalContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 30 }}>
            <div>
              {selectedRole?.assignedTo === user?._id ? (
                <Message color="blue">
                  <MessageHeader>This is you!</MessageHeader>
                  <MessageContent>
                    You are the support contact for this role
                  </MessageContent>
                </Message>
              ) : null}
              <p>
                You can contact{" "}
                {selectedRole?.ownerAssignedTo?.profile?.fullName} by sending
                them a direct message on
                {util.appName()}
              </p>
              <Button
                disabled={selectedRole?.assignedTo === user?._id}
                onClick={() => {
                  if (!selectedRole?.assignedTo) return;
                  setSelectedRole(null);
                  createChat([selectedRole.assignedTo]);
                }}
              >
                Send message
              </Button>
            </div>
            {assignedUserEmail ? (
              <div>
                <p>Alternatively, you can send them an email directly to:</p>
                <Input
                  readOnly
                  action={
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(assignedUserEmail);
                      }}
                    >
                      <Icon icon={CopyIcon} />
                    </Button>
                  }
                  value={assignedUserEmail}
                />
              </div>
            ) : null}
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setSelectedRole(null);
            }}
          >
            Done
          </Button>
        </ModalActions>
      </Modal>
      <MessageHeader>Need help?</MessageHeader>
      <MessageContent>
        <p>
          If you need help or have a specific query please contact{" "}
          {util.pluralise(
            pointsOfContact.length,
            "this user.",
            "one of the following users.",
            false,
          )}
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Table selectable style={{ maxWidth: 450 }}>
            <TableBody>
              {pointsOfContact.map((role) => (
                <TableRow
                  key={role.id}
                  onClick={() => {
                    setSelectedRole(role);
                  }}
                >
                  <TableCell>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          justifyContent: "space-between",
                        }}
                      >
                        <span>
                          For
                          <H3 style={{ margin: 0 }}>{role.name}</H3>
                        </span>
                        <UserChip user={role.ownerAssignedTo} />
                      </div>
                      <p style={{ whiteSpace: "pre-wrap" }}>
                        {role.description}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </MessageContent>
    </Message>
  );
};

export default PointOfContactList;
